import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import UserAvatar from './UserAvatar';
import { Target, Zap, Sparkles, RefreshCw, Loader2, Camera, CheckCircle2, AlertTriangle, XCircle, Minus, ClipboardPlus, TrendingUp, TrendingDown, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * L10 Section Content Component
 * Renders the content for each L10 meeting section (Segue, Scorecard, Rocks, etc.)
 * Extracted from EOSL10MeetingRoom.js for better maintainability
 */
const L10SectionContent = ({ 
  section, 
  data, 
  onGenerateSummary, 
  onGenerateUpdates, 
  aiLoading = false, 
  copilotEnabled = false,
  meetingId = null,
  onDataRefresh = null // Callback to refresh EOS data after inline updates
}) => {
  const [snapshotDialogOpen, setSnapshotDialogOpen] = useState(false);
  const [snapshotNotes, setSnapshotNotes] = useState('');
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [createIssueDialogOpen, setCreateIssueDialogOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [updatingIssueId, setUpdatingIssueId] = useState(null);
  const [updatingTodoId, setUpdatingTodoId] = useState(null);

  // Helper functions for scorecard display
  const getStatusIcon = (status) => {
    switch (status) {
      case 'green': return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'yellow': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'red': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-3 w-3 text-green-600" />;
      case 'down': return <TrendingDown className="h-3 w-3 text-red-600" />;
      default: return <Minus className="h-3 w-3 text-gray-400" />;
    }
  };

  const formatValue = (value, type) => {
    if (value === null || value === undefined) return '—';
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
      case 'percentage':
        return `${value}%`;
      default:
        return typeof value === 'number' ? value.toLocaleString() : value;
    }
  };

  const handleFreezeScorecard = async () => {
    if (!meetingId) {
      toast.error('No meeting ID available');
      return;
    }
    
    setSnapshotLoading(true);
    try {
      await axios.post(`${API}/eos/live-scorecard/snapshot`, {
        meeting_id: meetingId,
        notes: snapshotNotes
      });
      toast.success('Scorecard snapshot saved for this meeting!');
      setSnapshotDialogOpen(false);
      setSnapshotNotes('');
    } catch (error) {
      console.error('Error creating snapshot:', error);
      toast.error('Failed to create snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const handleCreateIssue = async () => {
    if (!selectedMetric) return;
    
    try {
      const issueData = {
        title: `${selectedMetric.name} Off Track`,
        description: `The ${selectedMetric.name} metric is currently off track.\n\nWeekly: ${formatValue(selectedMetric.weekly?.actual, selectedMetric.measurement_type)} (Goal: ${formatValue(selectedMetric.weekly?.goal, selectedMetric.measurement_type)})`,
        priority: selectedMetric.weekly?.status === 'red' ? 'high' : 'medium',
        category: selectedMetric.category,
        status: 'open'
      };
      
      await axios.post(`${API}/eos/issues`, issueData);
      toast.success('Issue created - discuss in IDS!');
      setCreateIssueDialogOpen(false);
      setSelectedMetric(null);
    } catch (error) {
      console.error('Error creating issue:', error);
      toast.error('Failed to create issue');
    }
  };

  // ============================================================================
  // FIX 1: IDS Inline Actions - Update issue status
  // ============================================================================
  const handleUpdateIssueStatus = async (issueId, newStatus) => {
    setUpdatingIssueId(issueId);
    try {
      await axios.put(`${API}/eos/issues/${issueId}`, { status: newStatus });
      toast.success(newStatus === 'solved' ? 'Issue solved! ✓' : 'Issue marked as discussing');
      // Refresh data if callback provided
      if (onDataRefresh) onDataRefresh();
    } catch (error) {
      console.error('Error updating issue status:', error);
      toast.error('Failed to update issue');
    } finally {
      setUpdatingIssueId(null);
    }
  };

  // ============================================================================
  // FIX 2: To-Do Complete Toggle - Toggle todo status
  // ============================================================================
  const handleToggleTodoStatus = async (todoId, currentStatus) => {
    setUpdatingTodoId(todoId);
    try {
      if (currentStatus === 'complete') {
        // Reopen the todo
        await axios.patch(`${API}/eos/todos/${todoId}/reopen`);
        toast.success('To-do reopened');
      } else {
        // Complete the todo
        await axios.patch(`${API}/eos/todos/${todoId}/complete`);
        toast.success('To-do completed! ✓');
      }
      // Refresh data if callback provided
      if (onDataRefresh) onDataRefresh();
    } catch (error) {
      console.error('Error toggling todo status:', error);
      toast.error('Failed to update to-do');
    } finally {
      setUpdatingTodoId(null);
    }
  };

  // Render based on section type
  switch (section) {
    case 'segue':
      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Good News - Personal & Professional</h3>
          <p className="text-xs text-muted-foreground">Share one personal and one professional win.</p>
          <Textarea placeholder="Your wins..." rows={3} className="text-sm" />
        </div>
      );

    case 'scorecard':
      const summary = data?.summary || {};
      const metrics = data?.metrics || [];
      
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Live Scorecard</h3>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSnapshotDialogOpen(true)}
              className="gap-1 text-xs"
            >
              <Camera className="h-3 w-3" />
              Freeze
            </Button>
          </div>
          
          {/* Summary Row */}
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400">
              {summary.green || 0} On Track
            </Badge>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400">
              {summary.yellow || 0} Warning
            </Badge>
            <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400">
              {summary.red || 0} Off Track
            </Badge>
          </div>
          
          <ScrollArea className="h-[250px]">
            {metrics.length > 0 ? (
              <div className="space-y-1.5">
                {metrics.map((m, i) => {
                  const isOffTrack = m.weekly?.status === 'red' || m.weekly?.status === 'yellow';
                  return (
                    <div 
                      key={i} 
                      className={`p-2 border rounded-lg flex justify-between items-center ${
                        m.weekly?.status === 'red' ? 'bg-red-50 dark:bg-red-950/30 border-red-200' : 
                        m.weekly?.status === 'yellow' ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200' :
                        m.weekly?.status === 'green' ? 'bg-green-50 dark:bg-green-950/30 border-green-200' : ''
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {getStatusIcon(m.weekly?.status)}
                        <div>
                          <div className="font-medium text-sm">{m.name}</div>
                          <div className="text-[10px] text-muted-foreground capitalize">{m.category}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-sm font-bold">{formatValue(m.weekly?.actual, m.measurement_type)}</div>
                          {m.weekly?.goal && (
                            <div className="text-[10px] text-muted-foreground">Goal: {formatValue(m.weekly?.goal, m.measurement_type)}</div>
                          )}
                        </div>
                        {getTrendIcon(m.weekly?.trend)}
                        {isOffTrack && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setSelectedMetric(m);
                              setCreateIssueDialogOpen(true);
                            }}
                          >
                            <ClipboardPlus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No metrics</div>
            )}
          </ScrollArea>
          
          {/* Freeze Scorecard Dialog */}
          <Dialog open={snapshotDialogOpen} onOpenChange={setSnapshotDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Freeze Scorecard</DialogTitle>
                <DialogDescription>
                  Save a snapshot of current scorecard values for this meeting
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm">Notes (Optional)</Label>
                  <Textarea
                    placeholder="Add any notes about this snapshot..."
                    value={snapshotNotes}
                    onChange={(e) => setSnapshotNotes(e.target.value)}
                    rows={3}
                  />
                </div>
                <div className="p-3 bg-muted rounded-lg text-xs">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="font-medium">Snapshot includes:</span>
                  </div>
                  <ul className="list-disc list-inside text-muted-foreground space-y-1">
                    <li>{metrics.length} metrics with Weekly, MTD, QTD, YTD values</li>
                    <li>Status indicators and trends</li>
                    <li>Timestamp and meeting reference</li>
                  </ul>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSnapshotDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleFreezeScorecard} disabled={snapshotLoading}>
                  {snapshotLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                  Freeze Scorecard
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Create Issue Dialog */}
          <Dialog open={createIssueDialogOpen} onOpenChange={setCreateIssueDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Issue from Metric</DialogTitle>
                <DialogDescription>
                  Add this off-track metric to the IDS list
                </DialogDescription>
              </DialogHeader>
              {selectedMetric && (
                <div className="space-y-4">
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(selectedMetric.weekly?.status)}
                      <span className="font-semibold">{selectedMetric.name}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground">Current:</span>
                        <span className="ml-2 font-medium">{formatValue(selectedMetric.weekly?.actual, selectedMetric.measurement_type)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Goal:</span>
                        <span className="ml-2 font-medium">{formatValue(selectedMetric.weekly?.goal, selectedMetric.measurement_type)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateIssueDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateIssue}>
                  <ClipboardPlus className="h-4 w-4 mr-2" />
                  Create Issue
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );

    case 'rocks':
      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Rocks</h3>
          <ScrollArea className="h-[250px]">
            {data?.rocks?.filter(r => r.status !== 'complete').length > 0 ? (
              <div className="space-y-2">
                {data.rocks.filter(r => r.status !== 'complete').slice(0, 8).map((r, i) => (
                  <div key={i} className="p-2 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-sm">{r.title}</span>
                      </div>
                      <Badge variant={r.status === 'on_track' ? 'default' : 'destructive'} className="text-xs">
                        {r.progress_percentage || 0}%
                      </Badge>
                    </div>
                    {r.owner_display && <UserAvatar user={r.owner_display} size="xs" showName className="mt-1" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No rocks</div>
            )}
          </ScrollArea>
        </div>
      );

    case 'headlines':
      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Headlines</h3>
          <ScrollArea className="h-[250px]">
            {data?.items?.length > 0 ? (
              <div className="space-y-2">
                {data.items.map((h, i) => (
                  <div key={i} className="p-2 border rounded-lg">
                    <div className="text-sm">{h.message}</div>
                    {h.author_display && <UserAvatar user={h.author_display} size="xs" showName className="mt-1" />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No headlines</div>
            )}
          </ScrollArea>
        </div>
      );

    case 'todos':
      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">To-Dos</h3>
          <p className="text-xs text-muted-foreground mb-2">Click checkbox to mark complete or reopen</p>
          <ScrollArea className="h-[250px]">
            {data?.items?.length > 0 ? (
              <div className="space-y-2">
                {data.items.map((t, i) => (
                  <div 
                    key={t.id || i} 
                    className={`flex items-center gap-2 p-2 border rounded-lg transition-all ${
                      t.status === 'complete' ? 'bg-green-500/10 border-green-500/30' : ''
                    } ${updatingTodoId === t.id ? 'opacity-50' : ''}`}
                  >
                    <Checkbox 
                      checked={t.status === 'complete'} 
                      disabled={updatingTodoId === t.id}
                      onCheckedChange={() => handleToggleTodoStatus(t.id, t.status)}
                      className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                    />
                    <div className="flex-1">
                      <div className={`text-sm ${t.status === 'complete' ? 'line-through text-muted-foreground' : ''}`}>
                        {t.task}
                      </div>
                      {t.owner_display && <UserAvatar user={t.owner_display} size="xs" showName className="mt-1" />}
                    </div>
                    <Badge 
                      variant={t.status === 'complete' ? 'default' : t.status === 'overdue' ? 'destructive' : 'outline'} 
                      className={`text-xs ${t.status === 'complete' ? 'bg-green-600' : ''}`}
                    >
                      {updatingTodoId === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : t.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">No to-dos</div>
            )}
          </ScrollArea>
        </div>
      );

    case 'ids':
      // Show both open issues and recently solved (to see progress)
      const openIssues = data?.issues?.filter(i => i.status !== 'solved' && i.status !== 'archived') || [];
      const solvedIssues = data?.issues?.filter(i => i.status === 'solved') || [];
      
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">IDS (Identify, Discuss, Solve)</h3>
            <div className="flex gap-2">
              <Badge variant="outline" className="text-xs">
                {openIssues.length} open
              </Badge>
              {solvedIssues.length > 0 && (
                <Badge variant="default" className="text-xs bg-green-600">
                  {solvedIssues.length} solved
                </Badge>
              )}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Click &quot;Discussing&quot; when starting, &quot;Solved ✓&quot; when resolved</p>
          <ScrollArea className="h-[280px]">
            {openIssues.length > 0 || solvedIssues.length > 0 ? (
              <div className="space-y-2">
                {/* Open Issues */}
                {openIssues.map((issue) => (
                  <div 
                    key={issue.id} 
                    className={`p-3 border rounded-lg transition-all ${
                      issue.status === 'in_discussion' 
                        ? 'border-yellow-500 bg-yellow-500/10' 
                        : 'border-slate-700'
                    } ${updatingIssueId === issue.id ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <Zap className={`h-4 w-4 flex-shrink-0 ${issue.priority === 'high' ? 'text-red-500' : 'text-yellow-500'}`} />
                      <span className="font-medium text-sm flex-1">{issue.title}</span>
                      <Badge variant={issue.priority === 'high' ? 'destructive' : 'outline'} className="text-xs">
                        {issue.priority}
                      </Badge>
                      {issue.status === 'in_discussion' && (
                        <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-500">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Discussing
                        </Badge>
                      )}
                    </div>
                    {issue.description && (
                      <p className="text-xs text-muted-foreground mt-1 ml-6">{issue.description}</p>
                    )}
                    {/* IDS Action Buttons */}
                    <div className="flex gap-2 mt-2 ml-6">
                      {issue.status === 'identified' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs border-yellow-500 text-yellow-500 hover:bg-yellow-500/20"
                          onClick={() => handleUpdateIssueStatus(issue.id, 'in_discussion')}
                          disabled={updatingIssueId === issue.id}
                        >
                          {updatingIssueId === issue.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <MessageSquare className="h-3 w-3 mr-1" />
                          )}
                          Discussing
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="h-7 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleUpdateIssueStatus(issue.id, 'solved')}
                        disabled={updatingIssueId === issue.id}
                      >
                        {updatingIssueId === issue.id ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        )}
                        Solved ✓
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Recently Solved Issues (collapsed) */}
                {solvedIssues.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-700">
                    <p className="text-xs text-green-500 font-medium mb-2">✓ Solved This Meeting</p>
                    {solvedIssues.slice(0, 3).map((issue) => (
                      <div 
                        key={issue.id} 
                        className="p-2 border rounded-lg border-green-500/30 bg-green-500/10 mb-1"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-muted-foreground line-through">{issue.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                No open issues - great job!
              </div>
            )}
          </ScrollArea>
        </div>
      );

    case 'conclude':
      return (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">Conclude</h3>
          <Textarea placeholder="Meeting recap..." rows={2} className="text-sm" />
          <Textarea placeholder="Cascading message..." rows={2} className="text-sm" />
          <div>
            <Label className="text-xs">Meeting Rating (1-10)</Label>
            <div className="flex gap-1 mt-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(r => (
                <Button key={r} variant="outline" size="sm" className="w-7 h-7 p-0">{r}</Button>
              ))}
            </div>
          </div>
          {copilotEnabled && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onGenerateSummary} disabled={aiLoading}>
                {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Generate Summary
              </Button>
              <Button variant="outline" size="sm" onClick={onGenerateUpdates} disabled={aiLoading}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Review Updates
              </Button>
            </div>
          )}
        </div>
      );

    default:
      return <div className="text-muted-foreground text-sm">Unknown section: {section}</div>;
  }
};

export default L10SectionContent;
